/**
 * Security utilities for consistent authentication and authorization checks
 * Used to prevent data exposure vulnerabilities across the application
 */

import { User } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";

/**
 * Requires user to be authenticated, throws error if not
 * Use at the beginning of functions that access user data
 */
export const requireAuth = (user: User | null): user is User => {
	if (!user?.id) {
		throw new Error("Authentication required - user must be logged in");
	}
	return true;
};

/**
 * Requires user to have mentor role
 */
export const requireMentorRole = (profile: any): boolean => {
	if (profile?.user_type !== "mentor") {
		throw new Error("Mentor privileges required");
	}
	return true;
};

/**
 * Requires user to have mentee role
 */
export const requireMenteeRole = (profile: any): boolean => {
	if (profile?.user_type !== "mentee") {
		throw new Error("Mentee privileges required");
	}
	return true;
};

/**
 * Requires user to have admin role
 */
export const requireAdminRole = (profile: any): boolean => {
	if (profile?.user_type !== "admin") {
		throw new Error("Admin privileges required");
	}
	return true;
};

/**
 * Verifies that a mentor has an accepted relationship with a specific mentee
 * Critical for mentor access to mentee data
 */
export const verifyMentorMenteeRelationship = async (
	mentorId: string,
	menteeId: string,
): Promise<boolean> => {
	try {
		const { data, error } = await supabase
			.from("mentor_mentee_relationships")
			.select("status")
			.eq("mentor_id", mentorId)
			.eq("mentee_id", menteeId)
			.eq("status", "accepted")
			.single();

		if (error) {
			console.error("Error verifying mentor-mentee relationship:", error);
			return false;
		}

		return !!data;
	} catch (error) {
		console.error("Error in verifyMentorMenteeRelationship:", error);
		return false;
	}
};

/**
 * Security-focused database query builder
 * Automatically adds user filtering to prevent data exposure
 */
export const createUserQuery = (tableName: string, userId: string) => {
	if (!userId) {
		throw new Error("User ID required for secure query");
	}

	return supabase.from(tableName).select("*").eq("user_id", userId);
};

/**
 * Security-focused database query for mentor accessing mentee data
 * Automatically verifies mentor-mentee relationship
 */
export const createMentorQuery = async (
	tableName: string,
	mentorId: string,
	menteeId: string,
) => {
	// First verify the relationship
	const hasRelationship = await verifyMentorMenteeRelationship(
		mentorId,
		menteeId,
	);

	if (!hasRelationship) {
		throw new Error("Unauthorized: No accepted mentor-mentee relationship");
	}

	return supabase.from(tableName).select("*").eq("user_id", menteeId);
};

/**
 * Logs security-related events for monitoring
 */
export const logSecurityEvent = (event: string, details: any) => {
	console.warn(`SECURITY EVENT: ${event}`, details);
	// In production, this would send to a security monitoring service
};

/**
 * Safe error handling that doesn't expose sensitive information
 */
export const createSafeError = (message: string): Error => {
	// Log the full error internally but return a safe message to the user
	logSecurityEvent("Safe error created", { message });
	return new Error("Access denied");
};

/**
 * Verifies that a user owns a specific group
 * Critical for group management operations
 */
export const verifyGroupOwnership = async (
	userId: string,
	groupId: string,
): Promise<boolean> => {
	try {
		if (!userId || !groupId) {
			return false;
		}

		const { data, error } = await supabase
			.from("groups")
			.select("created_by")
			.eq("id", groupId)
			.maybeSingle();

		if (error || !data) {
			console.error("Error verifying group ownership:", error);
			return false;
		}

		return data.created_by === userId;
	} catch (error) {
		console.error("Error in verifyGroupOwnership:", error);
		return false;
	}
};

/**
 * Verifies that a user can modify a group membership
 * User must either own the group or be the one who added the membership
 */
export const verifyGroupMembershipPermission = async (
	userId: string,
	membershipId: string,
): Promise<{ hasPermission: boolean; groupId?: string }> => {
	try {
		if (!userId || !membershipId) {
			return { hasPermission: false };
		}

		// Fetch membership to get group_id and added_by
		const { data: membership, error: membershipError } = await supabase
			.from("group_memberships")
			.select("group_id, added_by")
			.eq("id", membershipId)
			.maybeSingle();

		if (membershipError || !membership) {
			console.error("Error fetching membership:", membershipError);
			return { hasPermission: false };
		}

		// Check if user owns the group
		const ownsGroup = await verifyGroupOwnership(userId, membership.group_id);
		if (ownsGroup) {
			return { hasPermission: true, groupId: membership.group_id };
		}

		// Check if user added the membership
		if (membership.added_by === userId) {
			return { hasPermission: true, groupId: membership.group_id };
		}

		return { hasPermission: false, groupId: membership.group_id };
	} catch (error) {
		console.error("Error in verifyGroupMembershipPermission:", error);
		return { hasPermission: false };
	}
};

/**
 * Verifies that a mentor can access a specific reflection
 * Checks if the reflection belongs to one of the mentor's mentees
 */
export const verifyMentorReflectionAccess = async (
	mentorId: string,
	reflectionId: string,
): Promise<boolean> => {
	try {
		if (!mentorId || !reflectionId) {
			return false;
		}

		// First, get the reflection to find the mentee
		const { data: reflection, error: reflectionError } = await supabase
			.from("reflections")
			.select("user_id")
			.eq("id", reflectionId)
			.maybeSingle();

		if (reflectionError || !reflection) {
			console.error("Error fetching reflection:", reflectionError);
			return false;
		}

		// Verify mentor-mentee relationship
		return await verifyMentorMenteeRelationship(mentorId, reflection.user_id);
	} catch (error) {
		console.error("Error in verifyMentorReflectionAccess:", error);
		return false;
	}
};
